import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, FolderOpen, Loader2, RefreshCw } from "lucide-react";
import { NavBar } from "../components/NavBar";
import { listHistory, deleteHistory } from "../lib/api";
import { scoreColor } from "../lib/store";
import { useAuth } from "../lib/auth";

export default function History() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setItems(await listHistory());
    } catch (e) {
      setError(e.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (e, id) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await deleteHistory(id);
      toast.success("Deleted");
    } catch {
      load();
    }
  };

  return (
    <>
      <NavBar />
      <main className="container-nc" style={{ paddingTop: 40, paddingBottom: 90 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
          <div>
            <span className="eyebrow">Archive</span>
            <h1 style={{ fontSize: "clamp(2rem, 5vw, 3rem)", fontWeight: 600, letterSpacing: "-0.03em", margin: "8px 0 0" }}>
              History
            </h1>
          </div>
          <button className="btn-icon" onClick={load} title="Refresh" data-testid="history-refresh">
            <RefreshCw size={18} />
          </button>
        </div>

        {!isAuthed && (
          <div
            className="card card-pad"
            style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}
          >
            <span style={{ color: "var(--muted)", fontSize: 14 }}>
              You're browsing anonymously on this device. Sign in to sync your history with the iOS app and every device.
            </span>
            <button className="btn btn-ghost" onClick={() => navigate("/sign-in")} data-testid="history-signin">
              Sign in
            </button>
          </div>
        )}

        <div style={{ marginTop: 26 }}>
          {loading ? (
            <div style={{ display: "grid", placeItems: "center", padding: 80 }}>
              <Loader2 className="spin" size={26} color="var(--brand)" />
            </div>
          ) : error ? (
            <div style={{ display: "grid", placeItems: "center", padding: 60, gap: 14 }}>
              <p style={{ color: "var(--muted)" }}>{error}</p>
              <button className="btn btn-primary" onClick={load}>
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <div style={{ display: "grid", placeItems: "center", padding: 70, gap: 14, textAlign: "center" }}>
              <span
                style={{
                  width: 66,
                  height: 66,
                  borderRadius: "50%",
                  background: "var(--brand-tertiary)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <FolderOpen size={28} color="var(--brand)" />
              </span>
              <h3 style={{ margin: 0, fontWeight: 600 }}>No analyses yet</h3>
              <p style={{ color: "var(--muted)", margin: 0 }}>Your saved scorecards will appear here.</p>
              <button className="btn btn-primary" onClick={() => navigate("/app")} data-testid="empty-cta">
                Analyse an argument
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: 16,
              }}
            >
              {items.map((item) => {
                const higher = Math.max(item.person_a_score, item.person_b_score);
                const accent = scoreColor(higher);
                const d = new Date(item.created_at);
                const when = `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(
                  undefined,
                  { hour: "numeric", minute: "2-digit" }
                )}`;
                return (
                  <div
                    key={item.id}
                    className="card card-pad"
                    onClick={() => navigate(`/scorecard/${item.id}`)}
                    data-testid={`history-item-${item.id}`}
                    style={{ cursor: "pointer", transition: "border-color 0.2s", display: "flex", flexDirection: "column", gap: 14 }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--border-strong)")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: accent }} />
                      <span style={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.person_a_name} <span style={{ color: "var(--muted)", fontWeight: 400 }}>vs</span>{" "}
                        {item.person_b_name}
                      </span>
                      <button
                        className="btn-icon"
                        style={{ width: 32, height: 32, background: "transparent", border: "none" }}
                        onClick={(e) => onDelete(e, item.id)}
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 size={16} color="var(--muted)" />
                      </button>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                      <div style={{ flex: 1 }}>
                        <div className="gauge-num" style={{ fontSize: 30 }}>
                          {item.person_a_score}
                        </div>
                        <div className="eyebrow" style={{ marginTop: 2 }}>{item.person_a_name}</div>
                      </div>
                      <div style={{ width: 1, height: 34, background: "var(--border)" }} />
                      <div style={{ flex: 1 }}>
                        <div className="gauge-num" style={{ fontSize: 30, color: accent }}>
                          {item.person_b_score}
                        </div>
                        <div className="eyebrow" style={{ marginTop: 2 }}>{item.person_b_name}</div>
                      </div>
                    </div>
                    <p style={{ color: "var(--on-surface-2)", fontSize: 13.5, lineHeight: 1.5, margin: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {item.verdict}
                    </p>
                    <span className="mono" style={{ color: "var(--muted)", fontSize: 11 }}>{when}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
